import { useState, useEffect } from 'react';
import axios from 'axios';
import { clinicalServices } from '../services/endpoints/clinical';
import { DiseaseBreakdownCoverage, DiseaseBreakdownRow } from '../DiseaseBreakdownByIcd11Root';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export const useDiseaseBreakdown = (
    investigation: { uuid: string } | undefined,
    startDate: number | null,
    endDate: number | null,
    // SKIN-432 — the API resolves the ICD-11 root names, so it needs the
    // viewer's language. Taken as a parameter rather than read off the localize
    // context: `withLocalize` is how the other 89 files in this repo get it, and
    // VisitQueue.tsx records why (the context is absent from the package's type
    // definitions, so the HOC is the typed way in). The container wraps.
    language: string,
) => {
    const [rows, setRows] = useState<DiseaseBreakdownRow[]>([]);
    const [coverage, setCoverage] = useState<DiseaseBreakdownCoverage | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [reloadToken, setReloadToken] = useState(0);
    const retry = () => setReloadToken(t => t + 1);

    useEffect(() => {
        const source = axios.CancelToken.source();
        const superseded = () => Boolean(source.token.reason);

        const fetchData = async () => {
            if (!investigation?.uuid) return;
            const end = endDate ?? Date.now();
            const start = startDate ?? end - THIRTY_DAYS_MS;
            setLoading(true);
            setError(null);
            try {
                const result = await clinicalServices.getDiseaseBreakdown(
                    investigation.uuid,
                    start,
                    end,
                    language,
                    source.token,
                );
                if (superseded()) return;
                setRows(result.rows);
                setCoverage(result.coverage);
            } catch (err) {
                if (axios.isCancel(err) || superseded()) return;
                setError(err instanceof Error ? err : new Error('Unknown error'));
            } finally {
                if (!superseded()) setLoading(false);
            }
        };

        fetchData();
        return () => source.cancel('superseded');
    }, [investigation?.uuid, startDate, endDate, language, reloadToken]);

    return { rows, coverage, loading, error, retry };
};
